import type { Patient } from "@virtual-care/shared";
import { patientApiService } from "./patientApiService";

export class PatientService {
  /**
   * Fetch patients by unit ID from backend API
   * @param unitId - The unit ID to fetch patients for
   * @returns Promise<Patient[]>
   */
  async getPatients(unitId: string): Promise<Patient[]> {
    if (!unitId) {
      throw new Error("unitId is required to fetch patients");
    }
    return await patientApiService.getPatientsByUnit(unitId);
  }

  /**
   * Search patients by query (client-side filter)
   * @param query - Search query string
   * @param patients - Array of patients to search
   * @returns Filtered patients array
   */
  searchPatients(query: string, patients: Patient[]): Patient[] {
    if (!query || query.trim() === "") {
      return patients;
    }

    const lowerQuery = query.toLowerCase().trim();

    return patients.filter(
      (patient) =>
        patient.patientName.toLowerCase().includes(lowerQuery) ||
        patient.room.roomNumber.toLowerCase().includes(lowerQuery) ||
        patient.bed.bedNumber.toLowerCase().includes(lowerQuery) ||
        patient.primaryDiagnosis.toLowerCase().includes(lowerQuery) ||
        patient.reason.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Sort patients by column (client-side sort)
   * @param patients - Array of patients to sort
   * @param column - Column to sort by
   * @param direction - Sort direction (ASC or DESC)
   * @returns Sorted patients array
   */
  sortPatients(patients: Patient[], column: string | null, direction: "ASC" | "DESC" | null): Patient[] {
    if (!column || !direction) {
      return patients;
    }

    const sorted = [...patients].sort((a, b) => {
      let aValue: string | number | null = "";
      let bValue: string | number | null = "";

      switch (column) {
        case "reason":
          aValue = a.reason;
          bValue = b.reason;
          break;
        case "status":
          // Sort by discharge order time (most recent first when DESC)
          // Patients without discharge order time go to the end
          aValue = a.dischargeOrderTime ? new Date(a.dischargeOrderTime).getTime() : 0;
          bValue = b.dischargeOrderTime ? new Date(b.dischargeOrderTime).getTime() : 0;
          break;
        case "diagnosis":
          aValue = a.primaryDiagnosis;
          bValue = b.primaryDiagnosis;
          break;
        case "room":
          aValue = `${a.room.roomNumber}${a.bed.bedNumber}`;
          bValue = `${b.room.roomNumber}${b.bed.bedNumber}`;
          break;
        default:
          return 0;
      }

      // Handle null/empty values
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      if (aValue < bValue) return direction === "ASC" ? -1 : 1;
      if (aValue > bValue) return direction === "ASC" ? 1 : -1;
      return 0;
    });

    return sorted;
  }
}

export const patientService = new PatientService();
